import { useCallback, useMemo, useRef, useState } from 'react'
import SearchBar from '../../../../../../baseComponents/SearchBar'
import CustomSelect, { CustomSelectWithSearchBar } from '../../../../../../forms/CustomSelect'
import { addDictionaryExtraLine, dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import useOnClickOutside from '../../../../../../utils/hooks/useOnClickOut'
import TwinIcon from '../../../../../../baseComponents/TwinIcon'
import { faChevronLeft, faChevronRight, faSearch, faXmark } from '@fortawesome/pro-light-svg-icons'
import SearchBarProduct from '../../../../../Product/SearchBarProduct'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import { getEmployees } from '../../../../../../utils/reducers/getters'
import './workSheetProductStatusFlowSubHeaders.sass'
import { StatusFlowData } from '../WorkSheetProductStatusFlowInner/types'
import { WorkSheetGroupStatusesPayload } from '../../../../../../utils/reducers/sells/workSheetGroupStatus'


interface WorkSheetAllProductStatusFlowSubHeaderProps {
    data: StatusFlowData,
    handleSelectedData: (field: keyof StatusFlowData, value: any) => void
    WorkSheetGroupStatus: WorkSheetGroupStatusesPayload
}

export const WorkSheetAllProductStatusFlowSubHeader: React.FC<WorkSheetAllProductStatusFlowSubHeaderProps> = ({ data, handleSelectedData, WorkSheetGroupStatus }) => {
    const { t } = useTwinTranslation()
    const employees = getEmployees()
    const myEmployees = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(employees || {}))), [employees])
    const worksheetBy = {
        'ASC': { name: t('oldFirst', 'Antiguos primero') },
        'DESC': { name: t('newsFirst', 'Nuevos primero') },
    }
    
    return (
        <div className='flex items-end'>
            <WorkSheetGroupStatusSelector WorkSheetGroupStatus={WorkSheetGroupStatus} WorkSheetGroupStatusId={data.WorkSheetGroupStatusId} handleSelectedData={handleSelectedData} />
            <WorkSheetStatusProductSelector handleSelectedData={handleSelectedData} data={data} />
            <div className='flex items-end gap-x-30 flex-wrap gap-y-10 ml-auto'>
                <div className='table_searcher_header'>
                    <SearchBar className='search_bar_table' placeholder={t('searchDots', 'Buscar...')} searchValue={data.searchValue} onChange={(value) => handleSelectedData('searchValue', value)} />
                </div>
                <CustomSelectWithSearchBar sortItems={true} className='w-200' label={t('assignedEmployee', 'Empleado asignado')} value={data?.selectedEmployeeId || undefined} items={myEmployees || {}} fieldName={'fullname_short'} onChange={(value) => handleSelectedData('selectedEmployeeId', value)} name='AssignedEmployeeId'/>
                <CustomSelect className='w-200' label={t('worksheetBy', 'Ordenar por')} value={data.orderAscDesc} items={worksheetBy} onChange={(value) => handleSelectedData('orderAscDesc', value)} />
            </div>
        </div>
    )
}
interface WorkSheetProductStatusFlowSubHeaderProps {
    data: StatusFlowData,
    handleSelectedData: (field: keyof StatusFlowData, value: any) => void
}

export const WorkSheetProductStatusFlowSubHeader: React.FC<WorkSheetProductStatusFlowSubHeaderProps> = ({ data, handleSelectedData }) => {
    const { t } = useTwinTranslation()
    const employees = getEmployees()
    const myEmployees = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(employees || {}))), [employees])
    return (
        <div className='flex'>
            <WorkSheetStatusProductSelector handleSelectedData={handleSelectedData} data={data} />
            <div className='flex items-end gap-x-30 flex-wrap gap-y-10 ml-auto'>
                <div className='table_searcher_header'>
                    <SearchBar className='search_bar_table' placeholder={t('searchDots', 'Buscar...')} searchValue={data.searchValue} onChange={(value) => handleSelectedData('searchValue', value)} />
                </div>
                <CustomSelectWithSearchBar sortItems={true} className='w-200' label={t('assignedEmployee', 'Empleado asignado')} value={data?.selectedEmployeeId || undefined} items={myEmployees || {}} fieldName={'fullname_short'} onChange={(value) => handleSelectedData('selectedEmployeeId', value)} name='AssignedEmployeeId'/>
            </div>
        </div>
    )
}

interface WorkSheetGroupStatusSelectorProps extends WorkSheetGroupStatusSelectorLogicProps { }

const WorkSheetGroupStatusSelector: React.FC<WorkSheetGroupStatusSelectorProps> = ({ WorkSheetGroupStatus, WorkSheetGroupStatusId, ...rest }) => {
    const dictWorkSheetGroupStatusActive = dictionaryComplexFromJsonArr(WorkSheetGroupStatus?.WorkSheetActiveStatusGroups || [])
    const { nextWorkSheetGroupStatus, prevWorkSheetGroupStatus, onSelectWorkSheetGroupStatus } = useWorkSheetGroupStatusSelectorLogic({ WorkSheetGroupStatus, WorkSheetGroupStatusId, ...rest })
    return (
        <div className='flex items-center'>
            <div className='cursor-pointer hover:text-green-21 flex items-center' onClick={prevWorkSheetGroupStatus}><TwinIcon className='h-20' icon={faChevronLeft} /></div>
            <div className='cursor-pointer hover:text-green-21 ml-10 flex items-center' onClick={nextWorkSheetGroupStatus}><TwinIcon className='h-20' icon={faChevronRight} /></div>
            <div className='toolbar-label  text-left text-gray-51 capitalize mx-10 mt-1'>
                <h1 className='text-gray-51 text-18'>
                    <TwinTrans transKey={'group'}>Grupo</TwinTrans>:  {dictWorkSheetGroupStatusActive?.[WorkSheetGroupStatusId]?.name}
                </h1>
            </div>
            <CustomSelectWithSearchBar className='task_board_custom_field_flow_selector mr-24' label='' items={dictWorkSheetGroupStatusActive} onChange={onSelectWorkSheetGroupStatus} icon={faSearch} fieldName='name' />
        </div>
    )
}

interface WorkSheetGroupStatusSelectorLogicProps {
    WorkSheetGroupStatus: WorkSheetGroupStatusesPayload
    WorkSheetGroupStatusId: number
    handleSelectedData: (field: keyof StatusFlowData, value: any) => void
}

const useWorkSheetGroupStatusSelectorLogic = ({ WorkSheetGroupStatus, WorkSheetGroupStatusId, handleSelectedData }: WorkSheetGroupStatusSelectorLogicProps) => {
    const WorkSheetGroupStatusStr = JSON.stringify(WorkSheetGroupStatus)
    const positionsCPT = useMemo(() => {
        const myWorkSheetGroupStatus = JSON.parse(WorkSheetGroupStatusStr)
        let result: TwinDictionary = {
            byPos: {},
            byId: {},
            lastPos: 0
        }
        let pos = 0
        for (const element of myWorkSheetGroupStatus.WorkSheetActiveStatusGroups) {
            result.byPos[pos] = element.id
            result.byId[element.id] = pos
            pos++
        }
        result.lastPos = pos - 1
        return result
    }, [WorkSheetGroupStatusStr])

    const nextWorkSheetGroupStatus = useCallback(() => {
        const posId = positionsCPT.byId[WorkSheetGroupStatusId]
        const parsed = positionsCPT.byPos[posId + 1] || positionsCPT.byPos[0]
        handleSelectedData('WorkSheetGroupStatusId', parsed)
    }, [positionsCPT, WorkSheetGroupStatusId, handleSelectedData])

    const prevWorkSheetGroupStatus = useCallback(() => {
        const posId = positionsCPT.byId[WorkSheetGroupStatusId]
        const parsed = positionsCPT.byPos[posId - 1] || positionsCPT.byPos[positionsCPT.lastPos]
        handleSelectedData('WorkSheetGroupStatusId', parsed)
    }, [positionsCPT, WorkSheetGroupStatusId, handleSelectedData])

    const onSelectWorkSheetGroupStatus= useCallback((id: number) => {
        handleSelectedData('WorkSheetGroupStatusId', id)
    }, [handleSelectedData])

    return { nextWorkSheetGroupStatus, prevWorkSheetGroupStatus, onSelectWorkSheetGroupStatus }
}


interface WorkSheetStatusProductSelectorProps extends WorkSheetStatusProductSelectorLogicProps {
    data: StatusFlowData,
}
const WorkSheetStatusProductSelector: React.FC<WorkSheetStatusProductSelectorProps> = ({ data, handleSelectedData }) => {
    const { t } = useTwinTranslation()
    const { ref, handleShowProductSearchBar, showProductSearchBar, handleSelectedProduct } = useWorkSheetStatusProductSelectorLogic({ handleSelectedData })
    const selectedProduct = data.selectedProduct
    const title = selectedProduct ? selectedProduct.name : t('allProducts', 'Todos los productos')
    return (
        <div className='searchBarProductStatusSelector flex' ref={ref} >
            <div className='flex relative items-center'>
                <div onClick={handleShowProductSearchBar} className='flex items-center cursor-pointer'>
                    {selectedProduct?.url ? <img className='w-30 h-30 ml-10' src={selectedProduct?.url} alt={selectedProduct.name} />: null}
                    <div className='WorkSheet_status_product_selector_title mr-10 regular18'>{title}</div>
                    <TwinIcon icon={faSearch}></TwinIcon>
                </div>
                {selectedProduct ?
                    <div className='flex items-center ml-5 cursor-pointer' onClick={() => handleSelectedProduct(null)}><TwinIcon icon={faXmark}/></div>
                : null}
            </div>
            {showProductSearchBar ? 
                <ModalProductSearchBar handleSelectedProduct={handleSelectedProduct}/>
            : null}
    </div>
    )
}

interface WorkSheetStatusProductSelectorLogicProps {
    handleSelectedData: (field: keyof StatusFlowData, value: any) => void
}

const useWorkSheetStatusProductSelectorLogic = ({ handleSelectedData }: WorkSheetStatusProductSelectorLogicProps) => {
    const ref = useRef<any>(null)
    const [showProductSearchBar, setShowProductSearchBar] = useState<boolean>(false)
    
    const handleShowProductSearchBar = useCallback(() => {
        setShowProductSearchBar((old) => !old)
    }, [setShowProductSearchBar])

    const handleSelectedProduct = useCallback((value: any) => {
        handleSelectedData('selectedProduct', value)
        setShowProductSearchBar(false)
    }, [handleSelectedData, setShowProductSearchBar])

    useOnClickOutside(ref, () => setShowProductSearchBar(false))

    return { ref, showProductSearchBar, handleShowProductSearchBar, handleSelectedProduct }
}

interface ModalProductSearchBarProps {
    handleSelectedProduct: (value: any) => void
}
const ModalProductSearchBar: React.FC<ModalProductSearchBarProps> = ({ handleSelectedProduct }) => {
    const { t } = useTwinTranslation()
    return (
        <div className='worksheet_status_searcher_container p-0 absolute top-full'>
            <div className='worksheet_status_product_selector_searcher bg-white' >
                <SearchBarProduct className='searchBarProductStatusSelector' placeholder={t('searchProductsDots', 'Buscar productos...')} name='ProductId' changeSelectedItems={handleSelectedProduct} id={'0'} fieldNotIn='Product.id' url='/api/app/product/getAllProductsIncludingSubproducts' where={{}} order={[['Product"."ProductId', 'desc']]} />
            </div >
        </div>
    )
}